/**
 * Email Service
 * Handles sending emails for payment requests
 */

export interface EmailConfig {
  apiKey: string; // SendGrid, Mailgun, or similar API key
  fromEmail: string; // Your business email
  fromName: string; // Your business name
  service: 'sendgrid' | 'mailgun' | 'smtp';
}

export interface EmailMessage {
  to: string; // Customer email
  subject: string;
  html: string;
  text?: string; // Plain text version
}

class EmailService {
  private config: EmailConfig | null = null;
  private isInitialized = false;

  /**
   * Initialize Email service with credentials
   */
  async initialize(config: EmailConfig): Promise<void> {
    this.config = config;
    this.isInitialized = true;
    console.log('📧 Email Service initialized with', config.service);
  }

  /**
   * Send email message
   */
  async sendEmail(message: EmailMessage): Promise<{ success: boolean; messageId?: string; error?: string }> {
    if (!this.isInitialized || !this.config) {
      return {
        success: false,
        error: 'Email service not initialized. Please provide credentials.',
      };
    }

    try {
      console.log('📧 Sending email to:', message.to);
      console.log('📧 Subject:', message.subject);

      // In development, simulate email sending
      if (__DEV__) {
        console.log('🔧 [DEV MODE] Simulating email send...');
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        
        return {
          success: true,
          messageId: `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };
      }

      // Production: Send real email based on service
      switch (this.config.service) {
        case 'sendgrid':
          return this.sendViaSendGrid(message);
        case 'mailgun':
          return this.sendViaMailgun(message);
        case 'smtp':
          return this.sendViaSMTP(message);
        default:
          return {
            success: false,
            error: 'Unsupported email service',
          };
      }
    } catch (error: any) {
      console.error('❌ Email send error:', error);
      return {
        success: false,
        error: error.message || 'Network error sending email',
      };
    }
  }

  /**
   * Send via SendGrid
   */
  private async sendViaSendGrid(message: EmailMessage): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config!.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        personalizations: [{
          to: [{ email: message.to }],
          subject: message.subject,
        }],
        from: {
          email: this.config!.fromEmail,
          name: this.config!.fromName,
        },
        content: [
          {
            type: 'text/html',
            value: message.html,
          },
          ...(message.text ? [{
            type: 'text/plain',
            value: message.text,
          }] : []),
        ],
      }),
    });

    const data = await response.json();

    if (response.ok) {
      console.log('✅ Email sent successfully via SendGrid');
      return {
        success: true,
        messageId: `sg_${Date.now()}`,
      };
    } else {
      console.error('❌ SendGrid send failed:', data);
      return {
        success: false,
        error: data.errors?.[0]?.message || 'Failed to send email via SendGrid',
      };
    }
  }

  /**
   * Send via Mailgun
   */
  private async sendViaMailgun(message: EmailMessage): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const formData = new FormData();
    formData.append('from', `${this.config!.fromName} <${this.config!.fromEmail}>`);
    formData.append('to', message.to);
    formData.append('subject', message.subject);
    formData.append('html', message.html);
    if (message.text) {
      formData.append('text', message.text);
    }

    const response = await fetch(`https://api.mailgun.net/v3/${this.config!.apiKey.split('-')[1]}.mailgun.org/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`api:${this.config!.apiKey}`)}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (response.ok) {
      console.log('✅ Email sent successfully via Mailgun');
      return {
        success: true,
        messageId: data.id,
      };
    } else {
      console.error('❌ Mailgun send failed:', data);
      return {
        success: false,
        error: data.message || 'Failed to send email via Mailgun',
      };
    }
  }

  /**
   * Send via SMTP (basic implementation)
   */
  private async sendViaSMTP(message: EmailMessage): Promise<{ success: boolean; messageId?: string; error?: string }> {
    // This would require a backend service for SMTP
    // For now, simulate success
    console.log('📧 [SMTP] Simulating email send...');
    return {
      success: true,
      messageId: `smtp_${Date.now()}`,
    };
  }

  /**
   * Send payment request email
   */
  async sendPaymentRequestEmail(
    customerEmail: string,
    amount: number,
    serviceName: string,
    businessName: string,
    paymentOptions: {
      venmo?: string;
      paypal?: string;
      creditCard?: boolean;
    }
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const subject = `Payment Request - ${serviceName} - ${businessName}`;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Payment Request</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #007bff; color: white; padding: 20px; text-align: center; }
          .content { padding: 20px; background: #f8f9fa; }
          .payment-options { background: white; padding: 20px; margin: 20px 0; border-radius: 8px; }
          .payment-option { margin: 10px 0; padding: 10px; background: #e9ecef; border-radius: 4px; }
          .amount { font-size: 24px; font-weight: bold; color: #007bff; }
          .footer { text-align: center; padding: 20px; color: #6c757d; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Payment Request</h1>
            <p>From ${businessName}</p>
          </div>
          <div class="content">
            <h2>Service: ${serviceName}</h2>
            <div class="amount">Amount: $${amount}</div>
            
            <div class="payment-options">
              <h3>Payment Options:</h3>
              ${paymentOptions.venmo ? `<div class="payment-option"><strong>Venmo:</strong> ${paymentOptions.venmo}</div>` : ''}
              ${paymentOptions.paypal ? `<div class="payment-option"><strong>PayPal:</strong> ${paymentOptions.paypal}</div>` : ''}
              ${paymentOptions.creditCard ? `<div class="payment-option"><strong>Credit Card:</strong> Call us to pay</div>` : ''}
            </div>
            
            <p>Thank you for your business!</p>
          </div>
          <div class="footer">
            <p>This is an automated message from ${businessName}</p>
          </div>
        </div>
      </body>
      </html>
    `;

    const text = `
Payment Request - ${serviceName} - ${businessName}

Amount: $${amount}

Payment Options:
${paymentOptions.venmo ? `• Venmo: ${paymentOptions.venmo}` : ''}
${paymentOptions.paypal ? `• PayPal: ${paymentOptions.paypal}` : ''}
${paymentOptions.creditCard ? `• Credit Card: Call us to pay` : ''}

Thank you for your business!

This is an automated message from ${businessName}
    `;

    return this.sendEmail({
      to: customerEmail,
      subject,
      html,
      text,
    });
  }
}

// Export singleton instance
export const emailService = new EmailService();
export default emailService;